import { usePage } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FormError } from "@/components/FormError"

interface PageProps {
  fieldErrors?: Record<string, string>
  [key: string]: unknown
}

export default function ChangePassword() {
  const { t } = useTranslation()
  const { fieldErrors } = usePage<PageProps>().props
  const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content

  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [confirmError, setConfirmError] = useState<string | null>(null)

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    if (newPassword !== confirmPassword) {
      e.preventDefault()
      setConfirmError(t("changePassword.passwordsMustMatch"))
      return
    }
    setConfirmError(null)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{t("changePassword.title")}</CardTitle>
          <CardDescription>{t("changePassword.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form method="post" action="/change-password" onSubmit={handleSubmit} className="space-y-4">
            <input type="hidden" name="authenticity_token" value={csrfToken} />
            <div className="space-y-2">
              <Label htmlFor="password">{t("changePassword.currentPasswordLabel")}</Label>
              <Input
                id="password"
                name="password"
                type="password"
                required
              />
              <FormError error={fieldErrors?.password} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-password">{t("changePassword.newPasswordLabel")}</Label>
              <Input
                id="new-password"
                name="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
              />
              <FormError error={fieldErrors?.["new-password"]} />
              <p className="text-xs text-muted-foreground">{t("changePassword.passwordHint")}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password-confirm">{t("changePassword.confirmPasswordLabel")}</Label>
              <Input
                id="password-confirm"
                name="password-confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value)
                  setConfirmError(null)
                }}
                required
                minLength={8}
              />
              <FormError error={confirmError || fieldErrors?.["password-confirm"]} />
            </div>

            <Button type="submit" className="w-full">
              {t("changePassword.submit")}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              <a href="/dashboard" className="text-foreground hover:underline">
                {t("changePassword.backToApp")}
              </a>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
